import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '#environments';
import {
  CreateFdCaseRequest,
  CreateFdCaseResponse,
  FdBusinessPartnerEmail,
  FdCancelCaseRequest,
  FdCaseDetail,
  FdCaseListQuery,
  FdCasePage,
  FdContact,
  FdDigPlanApprovalRequest,
  FdSaveDigPlanApprovalRequest,
  FdEquipment,
  FdHistoricalPage,
  FdSaveAfeReviewRequest,
  FdSaveBusinessPartnerEmailRequest,
  FdSaveContactRequest,
  FdSaveDigPlanExecutionRequest,
  FdSaveEamRequest,
  FdSaveEnvironmentalReviewRequest,
  FdSaveEquipmentRequest,
  FdSaveJobCloseOutRequest,
  FdSaveJobExecutionRequest,
  FdSaveLandReviewRequest,
  FdSaveProjectDataEntryRequest,
  FdSaveProjectPermittingRequest,
  FdSaveSchedulingRequest,
  FdSaveSiteScopingRequest,
  FdSaveWbsAllocationRequest,
  FdSaveWellRequest,
  FdSiteReviewDecisionRequest,
  FdWbsAllocation,
  FdWell,
  FdAttachment,
  FdSchedulingQueueRow,
  AuditLogEntry,
} from '#models';

/**
 * HTTP client service for the FD (Facility Decommission) workflow API (M009).
 * Wraps every endpoint exposed by FdCasesController.
 * Injected into stage-page components and the stage queue.
 */
@Injectable({
  providedIn: 'root',
})
export class FdCasesApiService {
  private readonly _http = inject(HttpClient);
  private readonly _baseUrl = `${environment.apiUrl}/fd-cases`;
  private readonly _lookupUrl = `${environment.apiUrl}/lookup`;

  // ─── Lookups ─────────────────────────────────────────────────────────────────

  getLookupOptions(fieldKey: string): Observable<{ value: string }[]> {
    return this._http.get<{ value: string }[]>(`${this._lookupUrl}/${fieldKey}`);
  }

  // ─── Queries ─────────────────────────────────────────────────────────────────

  /** GET /api/fd-cases — paginated, FOT-scoped active case list. */
  getCases(query?: FdCaseListQuery): Observable<FdCasePage> {
    let params = new HttpParams();
    if (query?.stage)        params = params.set('stage', query.stage);
    if (query?.keyword)      params = params.set('keyword', query.keyword);
    if (query?.fot)          params = params.set('fot', query.fot);
    if (query?.scheduleCrew) params = params.set('scheduleCrew', query.scheduleCrew);
    if (query?.pageNumber != null) params = params.set('pageNumber', query.pageNumber);
    if (query?.pageSize   != null) params = params.set('pageSize', query.pageSize);
    return this._http.get<FdCasePage>(this._baseUrl, { params });
  }

  /** GET /api/fd-cases/historical — Admin-only completed case list (FR-070). */
  getHistoricalCases(pageNumber = 1, pageSize = 25): Observable<FdHistoricalPage> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize);
    return this._http.get<FdHistoricalPage>(`${this._baseUrl}/historical`, { params });
  }

  /** GET /api/fd-cases/{id} — full case detail. */
  getCase(id: number): Observable<FdCaseDetail> {
    return this._http.get<FdCaseDetail>(`${this._baseUrl}/${id}`);
  }

  /** GET /api/fd-cases/by-number/{caseNumber} — lookup by business case number. */
  getCaseByCaseNumber(caseNumber: string): Observable<FdCaseDetail> {
    return this._http.get<FdCaseDetail>(`${this._baseUrl}/by-number/${encodeURIComponent(caseNumber)}`);
  }

  /** GET /api/fd-cases/{id}/audit — audit log entries, newest-first. */
  getAuditLogs(id: number): Observable<AuditLogEntry[]> {
    return this._http.get<AuditLogEntry[]>(`${this._baseUrl}/${id}/audit`);
  }

  // ─── Case creation & cancellation ────────────────────────────────────────────

  /** POST /api/fd-cases — creates the FD case for an existing WorkflowCase (ADR-M9-001). */
  createCase(request: CreateFdCaseRequest): Observable<CreateFdCaseResponse> {
    return this._http.post<CreateFdCaseResponse>(this._baseUrl, request);
  }

  /** POST /api/fd-cases/{id}/cancel — cancels an FD case (FR-033). */
  cancelCase(id: number, request: FdCancelCaseRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/cancel`, request);
  }

  // ─── Stage 1: Project Data Entry ──────────────────────────────────────────────

  saveProjectDataEntry(id: number, request: FdSaveProjectDataEntryRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/project-data-entry/save`, request);
  }

  submitProjectDataEntry(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/project-data-entry/submit`, {});
  }

  // ─── Stage 2: Land Review ────────────────────────────────────────────────────

  saveLandReview(id: number, request: FdSaveLandReviewRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/land-review/save`, request);
  }

  submitLandReview(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/land-review/submit`, {});
  }

  // ─── Stage 3: Environmental Review ───────────────────────────────────────────

  saveEnvironmentalReview(id: number, request: FdSaveEnvironmentalReviewRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/environmental-review/save`, request);
  }

  submitEnvironmentalReview(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/environmental-review/submit`, {});
  }

  // ─── Stage 4: Site Scoping ────────────────────────────────────────────────────

  saveSiteScoping(id: number, request: FdSaveSiteScopingRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/site-scoping/save`, request);
  }

  submitSiteScoping(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/site-scoping/submit`, {});
  }

  // ─── Stage 5: Site Review ────────────────────────────────────────────────────

  /** POST /api/fd-cases/{id}/site-review/decision — FSMSupervisor Approve/Reject. */
  siteReviewDecision(id: number, request: FdSiteReviewDecisionRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/site-review/decision`, request);
  }

  // ─── Stage 6: AFE Review ─────────────────────────────────────────────────────

  saveAfeReview(id: number, request: FdSaveAfeReviewRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/afe-review/save`, request);
  }

  submitAfeReview(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/afe-review/submit`, {});
  }

  // ─── Stage 7: EAM ────────────────────────────────────────────────────────────

  saveEam(id: number, request: FdSaveEamRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/eam/save`, request);
  }

  submitEam(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/eam/submit`, {});
  }

  // ─── Stage 8: Project Permitting ─────────────────────────────────────────────

  saveProjectPermitting(id: number, request: FdSaveProjectPermittingRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/project-permitting/save`, request);
  }

  submitProjectPermitting(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/project-permitting/submit`, {});
  }

  // ─── Stage 9a: Dig Plan — Creation ───────────────────────────────────────────

  saveDigPlanCreation(id: number): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/dig-plan/creation/save`, {});
  }

  submitDigPlanCreation(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/dig-plan/creation/submit`, {});
  }

  // ─── Stage 9b: Dig Plan — Approval ───────────────────────────────────────────
  saveDigPlanApproval(id: number, request: FdSaveDigPlanApprovalRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/dig-plan/approval/save`, request);
  }
  /** POST /api/fd-cases/{id}/dig-plan/approval — FSMSupervisor Approve/Reject (FR-054). */
  digPlanApproval(id: number, request: FdDigPlanApprovalRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/dig-plan/approval`, request);
  }

  // ─── Stage 9c: Dig Plan — Execution ──────────────────────────────────────────

  saveDigPlanExecution(id: number, request: FdSaveDigPlanExecutionRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/dig-plan/execution/save`, request);
  }

  submitDigPlanExecution(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/dig-plan/execution/submit`, {});
  }

  // ─── Stage 10: Scheduling ────────────────────────────────────────────────────

  saveScheduling(id: number, request: FdSaveSchedulingRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/scheduling/save`, request);
  }

  submitScheduling(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/scheduling/submit`, {});
  }

  // ─── Stage 11: Job Execution ─────────────────────────────────────────────────

  saveJobExecution(id: number, request: FdSaveJobExecutionRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/job-execution/save`, request);
  }

  submitJobExecution(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/job-execution/submit`, {});
  }

  // ─── Stage 12: Job Close Out ─────────────────────────────────────────────────

  saveJobCloseOut(id: number, request: FdSaveJobCloseOutRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/job-close-out/save`, request);
  }

  submitJobCloseOut(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/job-close-out/submit`, {});
  }

  // ─── Child rows: Wells ────────────────────────────────────────────────────────

  addWell(id: number, request: FdSaveWellRequest): Observable<FdWell> {
    return this._http.post<FdWell>(`${this._baseUrl}/${id}/wells`, request);
  }

  deleteWell(id: number, wellId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/wells/${wellId}`);
  }

  // ─── Child rows: Equipment ────────────────────────────────────────────────────

  addEquipment(id: number, request: FdSaveEquipmentRequest): Observable<FdEquipment> {
    return this._http.post<FdEquipment>(`${this._baseUrl}/${id}/equipment`, request);
  }

  updateEquipment(id: number, equipmentId: number, request: FdSaveEquipmentRequest): Observable<FdEquipment> {
    return this._http.put<FdEquipment>(`${this._baseUrl}/${id}/equipment/${equipmentId}`, request);
  }

  deleteEquipment(id: number, equipmentId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/equipment/${equipmentId}`);
  }

  // ─── Child rows: Contacts ─────────────────────────────────────────────────────

  addContact(id: number, request: FdSaveContactRequest): Observable<FdContact> {
    return this._http.post<FdContact>(`${this._baseUrl}/${id}/contacts`, request);
  }

  updateContact(id: number, contactId: number, request: FdSaveContactRequest): Observable<FdContact> {
    return this._http.put<FdContact>(`${this._baseUrl}/${id}/contacts/${contactId}`, request);
  }

  deleteContact(id: number, contactId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/contacts/${contactId}`);
  }

  // ─── Child rows: WBS Allocations ─────────────────────────────────────────────

  addWbsAllocation(id: number, request: FdSaveWbsAllocationRequest): Observable<FdWbsAllocation> {
    return this._http.post<FdWbsAllocation>(`${this._baseUrl}/${id}/wbs-allocations`, request);
  }

  updateWbsAllocation(id: number, wbsId: number, request: FdSaveWbsAllocationRequest): Observable<FdWbsAllocation> {
    return this._http.put<FdWbsAllocation>(`${this._baseUrl}/${id}/wbs-allocations/${wbsId}`, request);
  }

  deleteWbsAllocation(id: number, wbsId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/wbs-allocations/${wbsId}`);
  }

  // ─── Child rows: Business Partner Emails ─────────────────────────────────────

  addBusinessPartnerEmail(id: number, request: FdSaveBusinessPartnerEmailRequest): Observable<FdBusinessPartnerEmail> {
    return this._http.post<FdBusinessPartnerEmail>(`${this._baseUrl}/${id}/business-partner-emails`, request);
  }

  deleteBusinessPartnerEmail(id: number, emailId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/business-partner-emails/${emailId}`);
  }

  // ─── Attachments ──────────────────────────────────────────────────────────────

  getAttachments(id: number): Observable<FdAttachment[]> {
    return this._http.get<FdAttachment[]>(`${this._baseUrl}/${id}/attachments`);
  }

  recalculateQueueDates(id: number, rows: FdSchedulingQueueRow[]): Observable<FdSchedulingQueueRow[]> {
    return this._http.post<FdSchedulingQueueRow[]>(`${this._baseUrl}/${id}/scheduling/recalculate-queue-dates`, rows);
  }

  initiateReclamation(id: number): Observable<{ caseNumber: string }> {
    return this._http.post<{ caseNumber: string }>(`${this._baseUrl}/${id}/initiate-reclamation`, null);
  }

  getAttachmentDownloadUrl(id: number, attachmentId: number): Observable<{ downloadUrl: string }> {
    return this._http.get<{ downloadUrl: string }>(`${this._baseUrl}/${id}/attachments/${attachmentId}/download`);
  }

  uploadAttachment(id: number, attachmentType: string, file: File): Observable<unknown> {
    const formData = new FormData();
    formData.append('attachmentType', attachmentType);
    formData.append('file', file, file.name);
    return this._http.post(`${this._baseUrl}/${id}/attachments`, formData);
  }

  deleteAttachment(id: number, attachmentId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/attachments/${attachmentId}`);
  }
}
